import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Phone, MapPin, Instagram, Send, CheckCircle, AlertCircle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Calendar } from '../components/ui/calendar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import axios from 'axios';
import { format, addDays, isBefore, startOfToday } from 'date-fns';
import { pt, enUS } from 'date-fns/locale';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 30 },
  whileInView: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: 'easeOut' },
  viewport: { once: true }
};

export default function Contact() {
  const { t, language } = useLanguage();
  const location = useLocation();
  const [activeTab, setActiveTab] = useState('contact');

  // Contact form state
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [contactStatus, setContactStatus] = useState({ type: '', message: '' });
  const [contactLoading, setContactLoading] = useState(false);

  // Booking form state
  const [bookingForm, setBookingForm] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    notes: ''
  });
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState('');
  const [availableTimes, setAvailableTimes] = useState([]);
  const [bookingStatus, setBookingStatus] = useState({ type: '', message: '' });
  const [bookingLoading, setBookingLoading] = useState(false);
  const [loadingTimes, setLoadingTimes] = useState(false);

  const services = [
    { value: 'family', label: t.services.family.title },
    { value: 'newborn', label: t.services.newborn.title },
    { value: 'maternity', label: t.services.maternity.title },
    { value: 'baptism', label: t.services.baptism.title },
  ];

  // Handle hash navigation
  useEffect(() => {
    if (location.hash === '#booking') {
      setActiveTab('booking');
    }
  }, [location]);

  // Fetch available times when date changes
  useEffect(() => {
    if (selectedDate) {
      fetchAvailableTimes(format(selectedDate, 'yyyy-MM-dd'));
    }
  }, [selectedDate]);

  const fetchAvailableTimes = async (date) => {
    setLoadingTimes(true);
    try {
      const response = await axios.get(`${API}/bookings/available-times?date=${date}`);
      setAvailableTimes(response.data.available_times);
    } catch (error) {
      console.error('Error fetching times:', error);
      setAvailableTimes([]);
    } finally {
      setLoadingTimes(false);
    }
  };

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactLoading(true);
    setContactStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/contact`, {
        ...contactForm,
        language
      });
      setContactStatus({ type: 'success', message: t.contact.form.success });
      setContactForm({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (error) {
      setContactStatus({ type: 'error', message: t.contact.form.error });
    } finally {
      setContactLoading(false);
    }
  };

  const handleBookingSubmit = async (e) => {
    e.preventDefault();
    if (!selectedDate || !selectedTime) return;

    setBookingLoading(true);
    setBookingStatus({ type: '', message: '' });

    try {
      await axios.post(`${API}/bookings`, {
        ...bookingForm,
        booking_date: format(selectedDate, 'yyyy-MM-dd'),
        booking_time: selectedTime,
        language
      });
      setBookingStatus({ type: 'success', message: t.booking.success });
      setBookingForm({ name: '', email: '', phone: '', service: '', notes: '' });
      setSelectedDate(null);
      setSelectedTime('');
    } catch (error) {
      const message = error.response?.data?.detail || t.booking.error;
      setBookingStatus({ type: 'error', message });
    } finally {
      setBookingLoading(false);
    }
  };

  const disabledDays = (date) => {
    return isBefore(date, startOfToday()) || date.getDay() === 0;
  };

  return (
    <div data-testid="contact-page" className="pt-24 bg-warm-50">
      {/* Header */}
      <section className="py-14 md:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <motion.div {...fadeUp} className="text-center">
            <span className="text-accent font-medium text-sm tracking-wider uppercase mb-3 block">
              {t.contact.subtitle}
            </span>
            <h1 className="font-heading text-4xl md:text-5xl font-semibold text-warm-900">
              {t.contact.title}
            </h1>
          </motion.div>
        </div>
      </section>

      {/* Tab Navigation */}
      <section className="py-4 bg-warm-100">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex justify-center gap-3">
            <button
              onClick={() => setActiveTab('contact')}
              data-testid="tab-contact"
              className={`px-8 py-3 text-sm font-medium rounded-full transition-all duration-300 ${
                activeTab === 'contact'
                  ? 'bg-accent text-white shadow-md'
                  : 'bg-white text-warm-600 hover:bg-warm-200'
              }`}
            >
              {t.contact.title}
            </button>
            <button
              onClick={() => setActiveTab('booking')}
              data-testid="tab-booking"
              className={`px-8 py-3 text-sm font-medium rounded-full transition-all duration-300 ${
                activeTab === 'booking'
                  ? 'bg-accent text-white shadow-md'
                  : 'bg-white text-warm-600 hover:bg-warm-200'
              }`}
            >
              {t.booking.title}
            </button>
          </div>
        </div>
      </section>

      {/* Contact Form */}
      {activeTab === 'contact' && (
        <section className="py-14 md:py-20 bg-warm-50">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
              {/* Form */}
              <motion.div {...fadeUp}>
                <p className="text-warm-600 text-lg leading-relaxed mb-8">
                  {t.contact.intro}
                </p>

                <form onSubmit={handleContactSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="name" className="text-warm-700 font-medium text-sm mb-2 block">
                        {t.contact.form.name} *
                      </Label>
                      <Input
                        id="name"
                        data-testid="contact-name"
                        value={contactForm.name}
                        onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                        required
                        className="bg-white border-warm-200 rounded-lg px-4 py-3 focus:border-accent focus:ring-accent text-warm-800"
                      />
                    </div>
                    <div>
                      <Label htmlFor="email" className="text-warm-700 font-medium text-sm mb-2 block">
                        {t.contact.form.email} *
                      </Label>
                      <Input
                        id="email"
                        type="email"
                        data-testid="contact-email"
                        value={contactForm.email}
                        onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                        required
                        className="bg-white border-warm-200 rounded-lg px-4 py-3 focus:border-accent focus:ring-accent text-warm-800"
                      />
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                      <Label htmlFor="phone" className="text-warm-700 font-medium text-sm mb-2 block">
                        {t.contact.form.phone}
                      </Label>
                      <Input
                        id="phone"
                        type="tel"
                        data-testid="contact-phone"
                        value={contactForm.phone}
                        onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                        className="bg-white border-warm-200 rounded-lg px-4 py-3 focus:border-accent focus:ring-accent text-warm-800"
                      />
                    </div>
                    <div>
                      <Label htmlFor="service" className="text-warm-700 font-medium text-sm mb-2 block">
                        {t.contact.form.service}
                      </Label>
                      <Select
                        value={contactForm.service}
                        onValueChange={(value) => setContactForm({ ...contactForm, service: value })}
                      >
                        <SelectTrigger 
                          data-testid="contact-service"
                          className="bg-white border-warm-200 rounded-lg px-4 py-3 focus:border-accent focus:ring-accent text-warm-800"
                        >
                          <SelectValue placeholder={t.contact.form.selectService} />
                        </SelectTrigger>
                        <SelectContent className="bg-white border-warm-200">
                          {services.map((service) => (
                            <SelectItem 
                              key={service.value} 
                              value={service.value}
                              className="text-warm-700 focus:bg-accent/10 focus:text-accent"
                            >
                              {service.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="message" className="text-warm-700 font-medium text-sm mb-2 block">
                      {t.contact.form.message} *
                    </Label>
                    <Textarea
                      id="message"
                      data-testid="contact-message"
                      value={contactForm.message}
                      onChange={(e) => setContactForm({ ...contactForm, message: e.target.value })}
                      required
                      rows={5}
                      placeholder={t.contact.form.messagePlaceholder}
                      className="bg-white border-warm-200 rounded-lg px-4 py-3 focus:border-accent focus:ring-accent text-warm-800 resize-none"
                    />
                  </div>

                  {contactStatus.message && (
                    <div
                      data-testid="contact-status"
                      className={`flex items-center gap-2 p-4 rounded-lg ${
                        contactStatus.type === 'success'
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}
                    >
                      {contactStatus.type === 'success' ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <AlertCircle className="w-5 h-5" />
                      )}
                      <span className="text-sm">{contactStatus.message}</span>
                    </div>
                  )}

                  <Button
                    type="submit"
                    data-testid="contact-submit"
                    disabled={contactLoading}
                    className="bg-accent hover:bg-accent-dark text-white font-medium px-8 py-3 rounded-full transition-all duration-300 hover:shadow-lg disabled:opacity-50"
                  >
                    {contactLoading ? t.contact.form.sending : t.contact.form.send}
                    <Send className="ml-2 w-4 h-4" />
                  </Button>
                </form>
              </motion.div>

              {/* Contact Info */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
                className="lg:pl-8"
              >
                <div className="bg-white rounded-2xl p-8 shadow-sm">
                  <div className="space-y-8">
                    <div>
                      <h3 className="text-accent font-heading font-semibold text-sm tracking-wide mb-4">
                        {t.contact.info.location}
                      </h3>
                      <div className="flex items-start gap-4 text-warm-600">
                        <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                          <MapPin className="w-5 h-5 text-accent" />
                        </div>
                        <span className="text-lg pt-2">{t.contact.info.locationValue}</span>
                      </div>
                    </div>

                    <div>
                      <h3 className="text-accent font-heading font-semibold text-sm tracking-wide mb-4">
                        {t.contact.info.email}
                      </h3>
                      <a
                        href="mailto:verahilariofotografia@gmail.com"
                        data-testid="contact-info-email"
                        className="flex items-center gap-4 text-warm-600 hover:text-accent transition-colors"
                      >
                        <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                          <Mail className="w-5 h-5 text-accent" />
                        </div>
                        <span className="text-base">verahilariofotografia@gmail.com</span>
                      </a>
                    </div>

                    <div>
                      <h3 className="text-accent font-heading font-semibold text-sm tracking-wide mb-4">
                        {t.contact.info.phone}
                      </h3>
                      <a
                        href="tel:+351913446144"
                        data-testid="contact-info-phone"
                        className="flex items-center gap-4 text-warm-600 hover:text-accent transition-colors"
                      >
                        <div className="w-10 h-10 rounded-full bg-accent/10 flex items-center justify-center flex-shrink-0">
                          <Phone className="w-5 h-5 text-accent" />
                        </div>
                        <span className="text-lg">+351 913 446 144</span>
                      </a>
                    </div>

                    <div>
                      <h3 className="text-accent font-heading font-semibold text-sm tracking-wide mb-4">
                        {t.contact.info.social}
                      </h3>
                      <a
                        href="https://www.instagram.com/verahilariofotografia/"
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid="contact-info-instagram"
                        className="inline-flex items-center gap-4 text-warm-600 hover:text-accent transition-colors group"
                      >
                        <div className="w-10 h-10 rounded-full bg-accent/10 group-hover:bg-accent/20 flex items-center justify-center transition-colors">
                          <Instagram className="w-5 h-5 text-accent" />
                        </div>
                        <span className="text-base">@verahilariofotografia</span>
                      </a>
                    </div>
                  </div>
                </div>
              </motion.div>
            </div>
          </div>
        </section>
      )}

      {/* Booking Form */}
      {activeTab === 'booking' && (
        <section className="py-14 md:py-20 bg-warm-50" id="booking">
          <div className="max-w-7xl mx-auto px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
              {/* Calendar */}
              <motion.div {...fadeUp}>
                <h3 className="text-accent font-heading font-semibold text-sm tracking-wide mb-6">
                  {t.booking.selectDate}
                </h3>
                <div className="bg-white rounded-2xl p-6 shadow-sm inline-block">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    disabled={disabledDays}
                    locale={language === 'pt' ? pt : enUS}
                    fromDate={addDays(new Date(), 1)}
                    toDate={addDays(new Date(), 90)}
                    className="text-warm-800"
                    data-testid="booking-calendar"
                  />
                </div>

                {selectedDate && (
                  <div className="mt-8">
                    <h3 className="text-accent font-heading font-semibold text-sm tracking-wide mb-4">
                      {t.booking.selectTime}
                    </h3>
                    {loadingTimes ? (
                      <p className="text-warm-500">Loading...</p>
                    ) : availableTimes.length > 0 ? (
                      <div className="flex flex-wrap gap-3">
                        {availableTimes.map((time) => (
                          <button
                            key={time}
                            onClick={() => setSelectedTime(time)}
                            data-testid={`time-slot-${time}`}
                            className={`px-5 py-2.5 text-sm font-medium rounded-full transition-all duration-300 ${
                              selectedTime === time
                                ? 'bg-accent text-white shadow-md'
                                : 'bg-white text-warm-600 hover:bg-warm-100 border border-warm-200'
                            }`}
                          >
                            {time}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-warm-500">{t.booking.noSlots}</p>
                    )}
                  </div>
                )}
              </motion.div>

              {/* Form */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.6 }}
                viewport={{ once: true }}
              >
                <h3 className="text-accent font-heading font-semibold text-sm tracking-wide mb-6">
                  {t.booking.yourInfo}
                </h3>

                <form onSubmit={handleBookingSubmit} className="space-y-5 bg-white rounded-2xl p-8 shadow-sm" data-testid="booking-form">
                  <div>
                    <Label htmlFor="booking-name" className="text-warm-700 font-medium text-sm mb-2 block">
                      {t.contact.form.name} *
                    </Label>
                    <Input
                      id="booking-name"
                      data-testid="booking-name"
                      value={bookingForm.name}
                      onChange={(e) => setBookingForm({ ...bookingForm, name: e.target.value })}
                      required
                      className="bg-warm-50 border-warm-200 rounded-lg px-4 py-3 focus:border-accent focus:ring-accent text-warm-800"
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                    <div>
                      <Label htmlFor="booking-email" className="text-warm-700 font-medium text-sm mb-2 block">
                        {t.contact.form.email} *
                      </Label>
                      <Input
                        id="booking-email"
                        type="email"
                        data-testid="booking-email"
                        value={bookingForm.email}
                        onChange={(e) => setBookingForm({ ...bookingForm, email: e.target.value })}
                        required
                        className="bg-warm-50 border-warm-200 rounded-lg px-4 py-3 focus:border-accent focus:ring-accent text-warm-800"
                      />
                    </div>
                    <div>
                      <Label htmlFor="booking-phone" className="text-warm-700 font-medium text-sm mb-2 block">
                        {t.contact.form.phone} *
                      </Label>
                      <Input
                        id="booking-phone"
                        type="tel"
                        data-testid="booking-phone"
                        value={bookingForm.phone}
                        onChange={(e) => setBookingForm({ ...bookingForm, phone: e.target.value })}
                        required
                        className="bg-warm-50 border-warm-200 rounded-lg px-4 py-3 focus:border-accent focus:ring-accent text-warm-800"
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="booking-service" className="text-warm-700 font-medium text-sm mb-2 block">
                      {t.contact.form.service} *
                    </Label>
                    <Select
                      value={bookingForm.service}
                      onValueChange={(value) => setBookingForm({ ...bookingForm, service: value })}
                      required
                    >
                      <SelectTrigger 
                        data-testid="booking-service"
                        className="bg-warm-50 border-warm-200 rounded-lg px-4 py-3 focus:border-accent focus:ring-accent text-warm-800"
                      >
                        <SelectValue placeholder={t.contact.form.selectService} />
                      </SelectTrigger>
                      <SelectContent className="bg-white border-warm-200">
                        {services.map((service) => (
                          <SelectItem 
                            key={service.value} 
                            value={service.value}
                            className="text-warm-700 focus:bg-accent/10 focus:text-accent"
                          >
                            {service.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div>
                    <Label htmlFor="booking-notes" className="text-warm-700 font-medium text-sm mb-2 block">
                      {t.booking.notes}
                    </Label>
                    <Textarea
                      id="booking-notes"
                      data-testid="booking-notes"
                      value={bookingForm.notes}
                      onChange={(e) => setBookingForm({ ...bookingForm, notes: e.target.value })}
                      rows={3}
                      placeholder={t.booking.notesPlaceholder}
                      className="bg-warm-50 border-warm-200 rounded-lg px-4 py-3 focus:border-accent focus:ring-accent text-warm-800 resize-none"
                    />
                  </div>

                  {bookingStatus.message && (
                    <div
                      data-testid="booking-status"
                      className={`flex items-center gap-2 p-4 rounded-lg ${
                        bookingStatus.type === 'success'
                          ? 'bg-green-50 text-green-700 border border-green-200'
                          : 'bg-red-50 text-red-700 border border-red-200'
                      }`}
                    >
                      {bookingStatus.type === 'success' ? (
                        <CheckCircle className="w-5 h-5" />
                      ) : (
                        <AlertCircle className="w-5 h-5" />
                      )}
                      <span className="text-sm">{bookingStatus.message}</span>
                    </div>
                  )}

                  <Button
                    type="submit"
                    data-testid="booking-submit"
                    disabled={bookingLoading || !selectedDate || !selectedTime || !bookingForm.service}
                    className="bg-accent hover:bg-accent-dark text-white font-medium px-8 py-3 rounded-full transition-all duration-300 hover:shadow-lg disabled:opacity-50 w-full"
                  >
                    {bookingLoading ? t.booking.confirming : t.booking.confirm}
                  </Button>
                </form>
              </motion.div>
            </div>
          </div>
        </section>
      )}
    </div>
  );
}
